import { useEffect, useState } from "react";
import { Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  instagramPostCdnFallback,
  instagramPostDisplayThumbnail,
  type InstagramPostThumbnailFields,
} from "@/lib/instagram-post-thumbnail";

type InstagramPostThumbnailProps = {
  post: InstagramPostThumbnailFields;
  presignedByPostId: Record<string, string>;
  size?: "sm" | "md";
  className?: string;
};

const SIZE_CLASS = {
  sm: "w-12 h-12",
  md: "w-16 h-16",
} as const;

export function InstagramPostThumbnail({
  post,
  presignedByPostId,
  size = "md",
  className,
}: InstagramPostThumbnailProps) {
  const [src, setSrc] = useState<string | null>(() =>
    instagramPostDisplayThumbnail(post, presignedByPostId)
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrc(instagramPostDisplayThumbnail(post, presignedByPostId));
    setFailed(false);
  }, [post, presignedByPostId]);

  const sizeClass = SIZE_CLASS[size];

  if (!src || failed) {
    return (
      <div
        className={cn(
          sizeClass,
          "rounded-lg bg-muted flex items-center justify-center flex-shrink-0",
          className,
        )}
      >
        <ImageIcon className={cn(size === "sm" ? "w-5 h-5" : "w-6 h-6", "text-muted-foreground")} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt="Post"
      className={cn(sizeClass, "rounded-lg object-cover flex-shrink-0", className)}
      onError={() => {
        const fallback = instagramPostCdnFallback(post);
        if (fallback && fallback !== src) {
          setSrc(fallback);
          return;
        }
        setFailed(true);
      }}
    />
  );
}
